import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { Edit01, Trash01, RefreshCw01, X as CloseX, Tag01 } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { Badge } from "@/components/base/badges/badges";
import { TagInput } from "@/components/base/input/tag-input";
import { useTranslation } from "react-i18next";
import {
    useKnowledgeDocument,
    useUpdateKnowledgeDocument,
    useDeleteKnowledgeDocument,
    useRegenerateEmbeddings,
} from "@/hooks/api/use-knowledge-bases";
import { useKnowledgeTags, useAutoTagDocument } from "@/hooks/api/use-knowledge-tags";
import type { KnowledgeDocumentAccessLevel, KnowledgeDocumentUpdateRequest } from "@/types/api/knowledge-bases";
import { useToast } from "@/hooks/use-toast";
import type { BadgeColors } from "@/components/base/badges/badge-types";

interface DocumentDetailDrawerProps {
    knowledgeBaseId: string;
    documentId: string;
    isOpen: boolean;
    onClose: () => void;
}

const SOURCE_TYPE_CONFIG: Record<string, { label: string; color: BadgeColors }> = {
    upload: { label: "Manual Upload", color: "gray" },
    seed_data: { label: "Seed Data", color: "gray" },
    training_email: { label: "Training Email", color: "blue" },
    review_resolution: { label: "Review Center", color: "success" },
    conversation: { label: "Agent Chat", color: "warning" },
    episode: { label: "Issue Episode", color: "brand" },
    agent_analysis: { label: "Agent Analysis", color: "brand" },
};

const getSourceConfig = (sourceType?: string) => {
    if (!sourceType) return { label: "Unknown", color: "gray" as BadgeColors };
    return SOURCE_TYPE_CONFIG[sourceType] || { label: sourceType, color: "gray" as BadgeColors };
};

const MIN_WIDTH = 380;
const DEFAULT_WIDTH = 480;
const MAX_WIDTH = 800;

type TabId = "metadata" | "content" | "chunks";

export const DocumentDetailDrawer = ({ knowledgeBaseId, documentId, isOpen, onClose }: DocumentDetailDrawerProps) => {
    const { t } = useTranslation(["pages:knowledge-admin", "common"]);
    const toast = useToast();
    const [activeTab, setActiveTab] = useState<TabId>("metadata");
    const [isEditing, setIsEditing] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [formData, setFormData] = useState<KnowledgeDocumentUpdateRequest>({});
    const [width, setWidth] = useState(DEFAULT_WIDTH);
    const isDragging = useRef(false);

    const { data: doc, isLoading } = useKnowledgeDocument(knowledgeBaseId, documentId);
    const updateDocument = useUpdateKnowledgeDocument();
    const deleteDocument = useDeleteKnowledgeDocument();
    const regenerateEmbeddings = useRegenerateEmbeddings();
    const autoTag = useAutoTagDocument();
    const { data: tagGroups = [] } = useKnowledgeTags();

    // Map color strings from API to valid BadgeColors
    const toBadgeColor = useCallback((color: string): BadgeColors => {
        const map: Record<string, BadgeColors> = {
            error: "error", warning: "warning", success: "success", brand: "brand",
            blue: "blue", indigo: "indigo", purple: "purple", pink: "pink",
            orange: "orange", gray: "gray", "blue-light": "blue-light", "gray-blue": "gray-blue",
        };
        return map[color] || "gray";
    }, []);

    /**
     * Group the document's JSONB tags by their canonical tag group.
     * Returns an array of { group, matchedTags } where matchedTags are the canonical tag objects.
     * Tags that don't match any canonical group go into an "Other" bucket.
     */
    // Group canonical tags by their group name (from API detail response)
    const groupedDocTags = useMemo(() => {
        const details = doc?.canonical_tags_detail;
        if (!details || details.length === 0) return [];

        const byGroup: Record<string, { group: string; label: string; color: string; tags: { slug: string; label: string }[] }> = {};
        for (const t of details) {
            if (!byGroup[t.group]) {
                // Find the group label from tagGroups if available
                const grp = tagGroups.find(g => g.name === t.group);
                byGroup[t.group] = { group: t.group, label: grp?.label || t.group, color: t.color, tags: [] };
            }
            byGroup[t.group].tags.push({ slug: t.slug, label: t.label });
        }

        return Object.values(byGroup);
    }, [doc?.canonical_tags_detail, tagGroups]);

    // Resize drag logic
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        isDragging.current = true;
        window.document.body.style.cursor = "col-resize";
        window.document.body.style.userSelect = "none";
    }, []);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isDragging.current) return;
            const newWidth = window.innerWidth - e.clientX;
            setWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, newWidth)));
        };
        const handleMouseUp = () => {
            if (isDragging.current) {
                isDragging.current = false;
                window.document.body.style.cursor = "";
                window.document.body.style.userSelect = "";
            }
        };
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, []);

    // Close on Escape
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        if (isOpen) window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onClose]);

    const startEditing = () => {
        if (!doc) return;
        setFormData({
            title: doc.title,
            content: doc.content,
            category: doc.category || [],
            topic: doc.topic || [],
            tags: doc.tags || [],
            priority: doc.priority,
            access_level: doc.access_level as KnowledgeDocumentAccessLevel,
            language: doc.language,
        });
        setIsEditing(true);
    };

    const handleSave = async () => {
        try {
            await updateDocument.mutateAsync({ knowledgeBaseId, documentId, data: formData });
            toast.success(t("documentDetail.messages.updateSuccess", { defaultValue: "Document updated successfully" }));
            setIsEditing(false);
        } catch {
            toast.error(t("documentDetail.messages.updateError", { defaultValue: "Error updating document" }));
        }
    };

    const handleDelete = async () => {
        try {
            await deleteDocument.mutateAsync({ knowledgeBaseId, documentId });
            toast.success(t("form.documents.deleteSuccess", { defaultValue: "Document deleted successfully" }));
            onClose();
        } catch {
            toast.error(t("form.documents.deleteError", { defaultValue: "Error deleting document" }));
        }
    };

    const handleRegenerate = async () => {
        try {
            const result = await regenerateEmbeddings.mutateAsync({ knowledgeBaseId, documentId });
            toast.success(
                t("documentDetail.messages.regenerateSuccess", {
                    defaultValue: `Embeddings regenerated: ${result.chunks_created} chunks created`,
                }),
            );
        } catch {
            toast.error(t("documentDetail.messages.regenerateError", { defaultValue: "Error regenerating embeddings" }));
        }
    };

    const handleAutoTag = async () => {
        try {
            const result = await autoTag.mutateAsync(documentId);
            if (result.total_new > 0) {
                toast.success(`Tagged: ${result.tags_assigned.join(", ")} (${result.total_new} new)`);
            } else {
                toast.success("No new tags to assign — document already well-tagged");
            }
        } catch {
            toast.error("Error auto-tagging document");
        }
    };

    const priorityOptions = [
        { id: "1", label: "1 - Lowest" },
        { id: "2", label: "2 - Low" },
        { id: "3", label: "3 - Medium" },
        { id: "4", label: "4 - High" },
        { id: "5", label: "5 - Highest" },
    ];

    const accessLevelOptions = [
        { id: "public", label: "Public" },
        { id: "company", label: "Company" },
        { id: "admin", label: "Admin" },
    ];

    const languageOptions = [
        { id: "en", label: "English" },
        { id: "it", label: "Italiano" },
        { id: "es", label: "Espanol" },
    ];

    const tabs: { id: TabId; label: string }[] = [
        { id: "metadata", label: t("documentDetail.tabs.metadata", { defaultValue: "Metadata" }) },
        { id: "content", label: t("documentDetail.tabs.content", { defaultValue: "Content" }) },
        { id: "chunks", label: t("documentDetail.tabs.chunks", { defaultValue: "Chunks" }) },
    ];

    const sourceConfig = getSourceConfig(doc?.source_type);

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div className="fixed inset-0 z-40 bg-overlay/50" onClick={onClose} />

            {/* Panel */}
            <div
                className="fixed inset-y-0 right-0 z-50 flex animate-in slide-in-from-right duration-200"
                style={{ width }}
            >
                {/* Drag handle */}
                <div
                    onMouseDown={handleMouseDown}
                    className="w-1.5 cursor-col-resize bg-transparent hover:bg-brand/30 active:bg-brand/50 transition-colors shrink-0"
                    title="Drag to resize"
                />

                {/* Content */}
                <div className="flex flex-1 flex-col bg-primary ring-1 ring-secondary_alt shadow-xl overflow-hidden">
                    {/* Header */}
                    <header className="relative w-full px-5 pt-5 pb-3 shrink-0">
                        {isLoading ? (
                            <div className="animate-pulse h-6 w-48 rounded bg-secondary" />
                        ) : doc ? (
                            <div className="space-y-2 pr-8">
                                <h3 className="text-lg font-semibold text-primary">{doc.title}</h3>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Badge color={sourceConfig.color} size="sm">{sourceConfig.label}</Badge>
                                    <Badge
                                        color={doc.access_level === "public" ? "success" : doc.access_level === "company" ? "blue" : "warning"}
                                        size="sm"
                                    >
                                        {doc.access_level}
                                    </Badge>
                                    <Badge color="gray" size="sm">Priority {doc.priority}</Badge>
                                </div>
                            </div>
                        ) : null}
                        <button
                            type="button"
                            onClick={() => { setIsEditing(false); onClose(); }}
                            className="absolute top-4 right-4 rounded-lg p-1 text-tertiary hover:text-primary hover:bg-secondary transition-colors"
                        >
                            <CloseX className="size-5" />
                        </button>
                    </header>

                    {/* Tabs */}
                    <div className="flex gap-1 border-b border-secondary px-5 shrink-0">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={`px-3 py-2 text-sm font-medium transition-colors ${
                                    activeTab === tab.id
                                        ? "border-b-2 border-brand text-brand"
                                        : "text-tertiary hover:text-primary"
                                }`}
                            >
                                {tab.label}
                                {tab.id === "chunks" && doc ? ` (${doc.chunk_count})` : ""}
                            </button>
                        ))}
                    </div>

                    {/* Tab Content */}
                    <div className="flex-1 overflow-y-auto px-5 py-4">
                        {isLoading ? (
                            <div className="flex items-center justify-center h-32">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand" />
                            </div>
                        ) : doc ? (
                            <>
                                {activeTab === "metadata" && (
                                    <div className="space-y-4">
                                        {isEditing ? (
                                            <div className="space-y-4">
                                                <Input
                                                    label={t("documentDetail.fields.title", { defaultValue: "Title" })}
                                                    value={formData.title || ""}
                                                    onChange={(value) => setFormData({ ...formData, title: value })}
                                                    size="md"
                                                />
                                                <TagInput
                                                    label={t("documentDetail.fields.category", { defaultValue: "Category" })}
                                                    value={formData.category || []}
                                                    onChange={(tags) => setFormData({ ...formData, category: tags })}
                                                    placeholder="Add category..."
                                                />
                                                <TagInput
                                                    label={t("documentDetail.fields.topic", { defaultValue: "Topic" })}
                                                    value={formData.topic || []}
                                                    onChange={(tags) => setFormData({ ...formData, topic: tags })}
                                                    placeholder="Add topic..."
                                                />
                                                <TagInput
                                                    label={t("documentDetail.fields.tags", { defaultValue: "Tags" })}
                                                    value={formData.tags || []}
                                                    onChange={(tags) => setFormData({ ...formData, tags: tags })}
                                                    placeholder="Add tag..."
                                                />
                                                <div className="grid grid-cols-2 gap-4">
                                                    <Select
                                                        label={t("documentDetail.fields.priority", { defaultValue: "Priority" })}
                                                        items={priorityOptions}
                                                        selectedKey={formData.priority?.toString() || "3"}
                                                        onSelectionChange={(key) =>
                                                            setFormData({ ...formData, priority: parseInt(key as string) })
                                                        }
                                                        size="md"
                                                    >
                                                        {(item) => (
                                                            <Select.Item key={item.id} id={item.id}>{item.label}</Select.Item>
                                                        )}
                                                    </Select>
                                                    <Select
                                                        label={t("documentDetail.fields.accessLevel", { defaultValue: "Access Level" })}
                                                        items={accessLevelOptions}
                                                        selectedKey={formData.access_level || "company"}
                                                        onSelectionChange={(key) =>
                                                            setFormData({ ...formData, access_level: key as KnowledgeDocumentAccessLevel })
                                                        }
                                                        size="md"
                                                    >
                                                        {(item) => (
                                                            <Select.Item key={item.id} id={item.id}>{item.label}</Select.Item>
                                                        )}
                                                    </Select>
                                                </div>
                                                <Select
                                                    label={t("documentDetail.fields.language", { defaultValue: "Language" })}
                                                    items={languageOptions}
                                                    selectedKey={formData.language || "en"}
                                                    onSelectionChange={(key) => setFormData({ ...formData, language: key as string })}
                                                    size="md"
                                                >
                                                    {(item) => (
                                                        <Select.Item key={item.id} id={item.id}>{item.label}</Select.Item>
                                                    )}
                                                </Select>
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                <MetadataRow label={t("documentDetail.fields.source", { defaultValue: "Source" })}>
                                                    <Badge color={sourceConfig.color} size="sm">{sourceConfig.label}</Badge>
                                                </MetadataRow>
                                                {doc.category && doc.category.length > 0 && (
                                                    <MetadataRow label={t("documentDetail.fields.category", { defaultValue: "Category" })}>
                                                        <div className="flex flex-wrap gap-1 justify-end">
                                                            {doc.category.map((cat) => (
                                                                <Badge key={cat} color="gray" size="sm">{cat}</Badge>
                                                            ))}
                                                        </div>
                                                    </MetadataRow>
                                                )}
                                                {doc.topic && doc.topic.length > 0 && (
                                                    <MetadataRow label={t("documentDetail.fields.topic", { defaultValue: "Topic" })}>
                                                        <div className="flex flex-wrap gap-1 justify-end">
                                                            {doc.topic.map((top) => (
                                                                <Badge key={top} color="gray" size="sm">{top}</Badge>
                                                            ))}
                                                        </div>
                                                    </MetadataRow>
                                                )}
                                                {groupedDocTags.length > 0 ? (
                                                    groupedDocTags.map((grp) => (
                                                        <MetadataRow key={grp.group} label={grp.label}>
                                                            <div className="flex flex-wrap gap-1 justify-end">
                                                                {grp.tags.map((tg) => (
                                                                    <Badge key={tg.slug} color={toBadgeColor(grp.color)} size="sm">{tg.label}</Badge>
                                                                ))}
                                                            </div>
                                                        </MetadataRow>
                                                    ))
                                                ) : (
                                                    <MetadataRow label="Tags">
                                                        <span className="text-xs text-tertiary italic">No tags — click Auto-tag</span>
                                                    </MetadataRow>
                                                )}
                                                <MetadataRow label={t("documentDetail.fields.priority", { defaultValue: "Priority" })}>
                                                    <span className="text-sm text-primary">{doc.priority}/5</span>
                                                </MetadataRow>
                                                <MetadataRow label={t("documentDetail.fields.accessLevel", { defaultValue: "Access Level" })}>
                                                    <Badge
                                                        color={doc.access_level === "public" ? "success" : doc.access_level === "company" ? "blue" : "warning"}
                                                        size="sm"
                                                    >
                                                        {doc.access_level}
                                                    </Badge>
                                                </MetadataRow>
                                                <MetadataRow label={t("documentDetail.fields.language", { defaultValue: "Language" })}>
                                                    <span className="text-sm text-primary">{doc.language}</span>
                                                </MetadataRow>
                                                {doc.version && (
                                                    <MetadataRow label={t("documentDetail.fields.version", { defaultValue: "Version" })}>
                                                        <span className="text-sm text-primary">{doc.version}</span>
                                                    </MetadataRow>
                                                )}
                                                <MetadataRow label={t("documentDetail.fields.retrievalCount", { defaultValue: "Retrievals" })}>
                                                    <span className="text-sm text-primary">{doc.retrieval_count}</span>
                                                </MetadataRow>
                                                <MetadataRow label={t("documentDetail.fields.chunkCount", { defaultValue: "Chunks" })}>
                                                    <span className="text-sm text-primary">{doc.chunk_count}</span>
                                                </MetadataRow>
                                                {doc.embedding_quality_score != null && (
                                                    <MetadataRow label={t("documentDetail.fields.embeddingQuality", { defaultValue: "Embedding Quality" })}>
                                                        <span className="text-sm text-primary">
                                                            {(doc.embedding_quality_score * 100).toFixed(0)}%
                                                        </span>
                                                    </MetadataRow>
                                                )}
                                                <MetadataRow label={t("documentDetail.fields.createdAt", { defaultValue: "Created" })}>
                                                    <span className="text-sm text-primary">{new Date(doc.created_at).toLocaleString()}</span>
                                                </MetadataRow>
                                                {doc.updated_at && (
                                                    <MetadataRow label={t("documentDetail.fields.updatedAt", { defaultValue: "Updated" })}>
                                                        <span className="text-sm text-primary">{new Date(doc.updated_at).toLocaleString()}</span>
                                                    </MetadataRow>
                                                )}
                                                {doc.last_retrieved_at && (
                                                    <MetadataRow label={t("documentDetail.fields.lastRetrieved", { defaultValue: "Last Retrieved" })}>
                                                        <span className="text-sm text-primary">{new Date(doc.last_retrieved_at).toLocaleString()}</span>
                                                    </MetadataRow>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {activeTab === "content" && (
                                    <div className="space-y-3">
                                        {isEditing ? (
                                            <>
                                                <div className="flex items-center gap-3 text-xs text-tertiary">
                                                    <span>{(formData.content?.length || 0).toLocaleString()} characters</span>
                                                    <span className="text-warning">Embedding will be regenerated on save</span>
                                                </div>
                                                <textarea
                                                    value={formData.content || ""}
                                                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                                    className="w-full min-h-[50vh] rounded-lg border border-primary bg-primary p-4 text-sm text-primary font-mono focus:border-brand focus:ring-4 focus:ring-brand/12 outline-none resize-y"
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <div className="flex items-center gap-3 text-xs text-tertiary">
                                                    <span>
                                                        {doc.content?.length.toLocaleString()}{" "}
                                                        {t("documentDetail.content.characters", { defaultValue: "characters" })}
                                                    </span>
                                                    <span>
                                                        {doc.content?.split(/\s+/).filter(Boolean).length.toLocaleString()}{" "}
                                                        {t("documentDetail.content.words", { defaultValue: "words" })}
                                                    </span>
                                                </div>
                                                <div className="max-h-[60vh] overflow-y-auto rounded-lg bg-secondary p-4 text-sm text-primary whitespace-pre-wrap">
                                                    {doc.content || t("documentDetail.content.empty", { defaultValue: "No content available" })}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                {activeTab === "chunks" && (
                                    <div className="space-y-2">
                                        {!doc.chunks || doc.chunks.length === 0 ? (
                                            <p className="text-sm text-tertiary text-center py-8">
                                                {t("documentDetail.chunks.noChunks", { defaultValue: "No chunks available" })}
                                            </p>
                                        ) : (
                                            doc.chunks.map((chunk) => (
                                                <ChunkCard
                                                    key={chunk.chunk_id}
                                                    index={chunk.chunk_index}
                                                    content={chunk.content}
                                                    retrievalCount={chunk.retrieval_count}
                                                />
                                            ))
                                        )}
                                    </div>
                                )}
                            </>
                        ) : null}
                    </div>

                    {/* Footer */}
                    {doc && (
                        <footer className="w-full px-5 py-3 shadow-[inset_0px_1px_0px_0px] shadow-border-secondary shrink-0">
                            {showDeleteConfirm ? (
                                <div className="space-y-3">
                                    <p className="text-sm text-primary">
                                        {t("documentDetail.messages.deleteConfirm", {
                                            defaultValue: "Are you sure you want to delete this document? This action cannot be undone.",
                                        })}
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <Button onClick={() => setShowDeleteConfirm(false)} size="sm" color="secondary">
                                            {t("form.cancel")}
                                        </Button>
                                        <Button onClick={handleDelete} size="sm" color="primary-destructive" isDisabled={deleteDocument.isPending}>
                                            {deleteDocument.isPending ? t("loading") : t("documentDetail.actions.confirmDelete", { defaultValue: "Confirm Delete" })}
                                        </Button>
                                    </div>
                                </div>
                            ) : isEditing ? (
                                <div className="flex items-center gap-2">
                                    <Button onClick={() => setIsEditing(false)} size="sm" color="secondary">{t("form.cancel")}</Button>
                                    <Button onClick={handleSave} size="sm" color="primary" isDisabled={updateDocument.isPending}>
                                        {updateDocument.isPending ? t("loading") : t("form.save")}
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Button onClick={handleAutoTag} size="sm" color="secondary" iconLeading={Tag01} isDisabled={autoTag.isPending}>
                                        {autoTag.isPending ? "Tagging..." : "Auto-tag"}
                                    </Button>
                                    <Button onClick={handleRegenerate} size="sm" color="secondary" iconLeading={RefreshCw01} isDisabled={regenerateEmbeddings.isPending}>
                                        {regenerateEmbeddings.isPending ? t("loading") : t("documentDetail.actions.regenerate", { defaultValue: "Re-embed" })}
                                    </Button>
                                    <Button onClick={startEditing} size="sm" color="secondary" iconLeading={Edit01}>
                                        {t("documentDetail.actions.edit", { defaultValue: "Edit" })}
                                    </Button>
                                    <Button onClick={() => setShowDeleteConfirm(true)} size="sm" color="secondary-destructive" iconLeading={Trash01}>
                                        {t("documentDetail.actions.delete", { defaultValue: "Delete" })}
                                    </Button>
                                </div>
                            )}
                        </footer>
                    )}
                </div>
            </div>
        </>
    );
};

const MetadataRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-secondary last:border-0">
        <span className="text-sm font-medium text-tertiary shrink-0">{label}</span>
        <div className="text-right">{children}</div>
    </div>
);

const ChunkCard = ({ index, content, retrievalCount }: { index: number; content: string; retrievalCount: number }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <div className="rounded-lg border border-secondary">
            <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-secondary/50 transition-colors rounded-lg"
            >
                <span className="text-sm font-medium text-primary">Chunk #{index + 1}</span>
                <span className="text-xs text-tertiary">{retrievalCount} retrievals</span>
            </button>
            {isExpanded && (
                <div className="px-3 pb-3 border-t border-secondary">
                    <div className="mt-2 rounded bg-secondary p-3 text-xs text-primary whitespace-pre-wrap max-h-48 overflow-y-auto">
                        {content}
                    </div>
                </div>
            )}
        </div>
    );
};
